import type { BreakDecisionOption } from '@runhuman/shared';
import {
  RISK_COLOR,
  formatBreakOptionHeading,
  formatCoverageGapLines,
  formatOptionDigestLine,
  riskGlyph,
} from './break-decision-option-format';
import type { BreakDecisionModalStyles } from './break-decision-modal-styles';

interface BreakDecisionAlternativeRowProps {
  readonly option: BreakDecisionOption;
  readonly timezone: string;
  readonly busy: boolean;
  readonly styles: BreakDecisionModalStyles;
  readonly onOffer: (option: BreakDecisionOption) => void;
  readonly onSchedule: (option: BreakDecisionOption) => void;
}

/**
 * One row inside {@link BreakDecisionModal}'s "Alternatives" section. Renders
 * the option's risk-decorated heading + rationale + on-duty/devices digest +
 * (optionally) coverage-gap lines, with two trailing actions:
 *
 *   - **Suggest** → admin proposes the alternative window; tester sees a
 *     `suggested` row and accepts/declines. (Underlying lifecycle status is
 *     literally `suggested`, hence the verb — matches the same word used
 *     elsewhere in the break-request surface.)
 *   - **Schedule** → admin directly approves the alternative; the request
 *     flips to `approved` with no tester round.
 */
export function BreakDecisionAlternativeRow({
  option,
  timezone,
  busy,
  styles,
  onOffer,
  onSchedule,
}: BreakDecisionAlternativeRowProps) {
  const heading = formatBreakOptionHeading(option, timezone);
  const digest = formatOptionDigestLine(option);
  const warnings = formatCoverageGapLines(option.digest.coverageGaps, timezone);
  const riskLevel = option.digest.riskLevel;
  return (
    <div style={styles.optionRow}>
      <div style={styles.optionBody}>
        <div style={styles.optionHeading}>
          <span style={{ color: RISK_COLOR[riskLevel], marginRight: 4 }} aria-hidden>
            {riskGlyph(riskLevel)}
          </span>
          {heading}
        </div>
        <div style={styles.optionRationale}>{option.rationale}</div>
        <div style={styles.optionDigest}>{digest}</div>
        {warnings.map((line) => (
          <div key={line} style={styles.optionWarning}>{line}</div>
        ))}
      </div>
      <div style={styles.rowActions}>
        <button
          type="button"
          style={styles.secondaryButton}
          disabled={busy}
          onClick={() => onOffer(option)}
          title="Suggest this window — the tester accepts or declines"
        >
          Suggest
        </button>
        <button
          type="button"
          style={styles.primaryButton}
          disabled={busy}
          onClick={() => onSchedule(option)}
          title="Schedule this window now (no tester round)"
        >
          Schedule
        </button>
      </div>
    </div>
  );
}
